import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';
import axios from '../utils/axios';
import waiverHtml from '../gym_app_liability_waiver';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [checkingWaiver, setCheckingWaiver] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    // Check if Google OAuth is configured
    const checkGoogleOAuthStatus = async () => {
      try {
        const response = await axios.get('/api/auth/google-status');
        setGoogleOAuthEnabled(response.data.googleOAuthEnabled);
      } catch (error) {
        console.error('Failed to check Google OAuth status:', error);
        setGoogleOAuthEnabled(false);
      }
    };

    checkGoogleOAuthStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Check waiver status
        try {
          const waiverResponse = await axios.get('/api/waiver/status');
          if (!waiverResponse.data.waiverAccepted) {
            setWaiverOpen(true);
            setLoading(false);
            return;
          }
        } catch (waiverError) {
          console.error('Error checking waiver status:', waiverError);
          // If error, still allow user to proceed but show waiver
          setWaiverOpen(true);
          setLoading(false);
          return;
        }
        navigate('/');
      } else {
        // Check if email verification is required
        if (result.requiresEmailVerification) {
          navigate('/verify-email', { 
            state: { 
              email: email,
              message: result.error,
              fromLogin: true
            } 
          });
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptWaiver = async () => {
    if (!waiverAccepted) {
      return;
    }

    try {
      setCheckingWaiver(true);
      await axios.post('/api/waiver/accept');
      setWaiverOpen(false);
      navigate('/');
    } catch (err) {
      console.error('Error accepting waiver:', err);
      alert('Failed to accept waiver. Please try again.');
    } finally {
      setCheckingWaiver(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Redirect to Google OAuth
    // Use relative URL since frontend and backend are on same domain
    const apiUrl = process.env.REACT_APP_API_URL;
    const baseUrl = (!apiUrl || apiUrl === '' || apiUrl === '""' || apiUrl.trim() === '') 
      ? window.location.origin 
      : apiUrl;
    window.location.href = `${baseUrl}/auth/google`;
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            or
          </Typography>
          <GoogleSignInButton
            onClick={handleGoogleSignIn}
            disabled={loading || !googleOAuthEnabled}
            fullWidth
          />
          {!googleOAuthEnabled && (
            <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', display: 'block' }}>
              Google sign-in requires setup. See GOOGLE_OAUTH_SETUP.md for instructions.
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2">
            Don't have an account?{' '}
            <Link href="/register" underline="hover">
              Register here
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <Link href="/forgot-password" underline="hover">
              Forgot your password?
            </Link>
          </Typography>
        </Box>
      </Paper>
      
      <Dialog 
        open={waiverOpen} 
        onClose={() => {}} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>Liability Waiver and Release Agreement</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 500, overflowY: 'auto' }}>
          <div dangerouslySetInnerHTML={{ __html: waiverHtml }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={waiverAccepted}
                onChange={(e) => setWaiverAccepted(e.target.checked)}
                color="primary"
                required
              />
            }
            label="I have read, understood, and agree to this liability waiver."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleAcceptWaiver} 
            variant="contained"
            color="primary"
            disabled={!waiverAccepted || checkingWaiver}
          >
            {checkingWaiver ? 'Processing...' : 'I Agree'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login; 
